/**
 * Created by piotr.pozniak@thebeaverhead.com on 13/10/2018.
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import EventList from "./EventsList/EventList";
import MonthlyView from "./MonthlyView/MonthlyView";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import WeeklyView from "./WeeklyView/WeeklyView";
import CardView from "./CardView/CardView";
import { CT, PT } from "../../../consts";
import {
  getFetchEventsParams,
  getSelectedDate,
  isCTAllowed,
} from "../../../helpers/calendar";
import CardSlider from "./CardView/CardSlider";
import { useEventsStore } from "../../../hooks/redux/events";
import IntegrationContext from "../strategies/IntegrationContext";
import { getAllOptionValues } from "../../../builder";
import { WT } from "../../admin/consts/widget";
import { useAppStore } from "../../../hooks/redux/app";
import classnames from "classnames";
import useAgressiveResponsiveWidget from "../../../hooks/useAgressiveResponsiveWidget";
import { filtersToQueryParam } from "../../../helpers/filters";
import DetailedList from "./DetailedList/DetailedList";
import { useCalendarStore } from "../../../hooks/redux/calendar";
import BubbleView from "./Bubble/BubbleView";
import FeaturedHighlights from "../FeaturedHighlights";
import { useFeaturedEventsStore } from "../../../hooks/redux/featuredEvents";
import WidgetSettingsContext from "../../../contexts/WidgetSettingsContext";
import BrandingHeader from "./Branding/BrandingHeader";
import { useTranslation } from "react-i18next";
import EventSchema from "./StructuredData/EventSchema";
import {
  getWidgetItemSlugFromUrl,
  getWidgetUUIDFromUrl,
} from "../../../helpers/linking";
import SelectedEventModal from "./SelectedEventModal";
import CalendarLinkingContext from "../../../contexts/CalendarLinkingContext";
import useEventLinking from "../hooks/useEventLinking";
import useQuery from "../../../hooks/useQuery";
import { getEndOfMonth, getStartOfMonth } from "../../../helpers/date";
import { calendarPublicName } from "../helpers";

const CalendarComponentsMapping = {
  [CT.card]: CardView,
  [CT.cardSlider]: CardSlider,
  [CT.cardList]: CardView,
  [CT.monthly]: MonthlyView,
  [CT.weekly]: WeeklyView,
  [CT.eventList]: EventList,
  [CT.detailedList]: DetailedList,
  [CT.bubble]: BubbleView,
};

/**
 * @param {Object} props
 * @param {Boolean} [embedded] - whether the calendar is embedded on a public site rather than previewed in the builder
 * @param {String} [singleEventSlug] - slug of a single event to deep-link to
 * @param {String} calendarUUID - the calendar's UUID
 * @param {String} [designSettings="design"] - which settings group to pull design options from
 */
const Calendar = ({
  embedded,
  singleEventSlug,
  calendarUUID,
  designSettings = "design",
}) => {
  const { calendar } = useCalendarStore();
  const [requestedWithSingleEvent, setRequestedWithSingleEvent] = useState(
    getWidgetItemSlugFromUrl("event")
  );

  //console.log("requestedWithSingleEvent", requestedWithSingleEvent);

  const hasValidProps = calendar.model.slug ? true : false;
  const hasSingleEvent =
    hasValidProps &&
    singleEventSlug &&
    calendar.model.uuid === getWidgetUUIDFromUrl(WT.calendar)
      ? true
      : false;
  // console.log("hasSingleEvent", hasSingleEvent, calendar?.model?.slug);

  const [searchKeyword, setSearchKeyword] = useState("");

  const {
    events,
    fetchEvents,
    fetchFeaturedEvents,
    fetchEvent,
    resetEventsCollection,
    searchEvents,
  } = useEventsStore();

  const { featuredEvents } = useFeaturedEventsStore();

  const { app } = useAppStore();
  const { i18n } = useTranslation();

  const widgetId = `dce_${WT.calendar}__${calendar.model.uuid}`;
  const query = useQuery();

  const integrationContext = useContext(IntegrationContext);

  const selectedEvent = useMemo(() => {
    if (singleEventSlug && calendar.model.uuid === query.get("calendar")) {
      return (
        events.collection.find((i) => i.slug === singleEventSlug) ||
        featuredEvents.collection.find((i) => i.slug === singleEventSlug)
      );
    }

    return null;
  }, [singleEventSlug, events.collection, featuredEvents.collection]);

  const calendarSettings = getAllOptionValues(calendar.model, designSettings);

  const calendarHighlightsSettings = getAllOptionValues(
    calendar.model,
    "highlights"
  );

  const calendarLinking = useEventLinking(
    calendar.model.uuid,
    selectedEvent,
    calendarSettings,
    calendarHighlightsSettings
  );

  const eventsNumberOption = calendarSettings["eventsNumberOption"] || "all";
  const eventsNumberCount = calendarSettings["eventsNumber"];
  const hasInfiniteScroll = calendarSettings["infiniteScroll"];
  const showMonthSelector = calendarSettings["showUpcomingNavigation"];
  const hasAggressiveResponsiveness =
    Number.parseInt(calendarSettings["WAggressiveResp"]) === 1;
  const showAI = calendarSettings["aiEnabled"];
  const aiPrefetchEvents = calendarSettings["aiPrefetchEvents"];
  const skipInitialFetch = showAI && aiPrefetchEvents === 0;

  // reset the events collection if kind of event filter has changed
  useDidMountEffect(() => {
    // reset collection only if model is available
    if (calendar.model.template) {
      resetEventsCollection();
    }
  }, [eventsNumberOption, eventsNumberCount, hasInfiniteScroll]);

  useAgressiveResponsiveWidget(
    widgetId,
    600,
    "dce-ar-responsiveness",
    hasAggressiveResponsiveness
  );

  const [state, setState] = useState({
    selectedDate: getSelectedDate(eventsNumberOption, eventsNumberCount),
    selectedFilters: integrationContext.availableFilters.reduce(
      (i, j) => ({ ...i, [j]: {} }),
      {}
    ),

    initialized: hasValidProps,
    page: 1,
  });

  const featuredHighlightsEventsLimit = useMemo(() => {
    return calendar.model.widget_settings.featured_events_limit || 0;
  }, [calendar.model.widget_settings.featured_events_limit]);

  useEffect(() => {
    if (calendar.model.slug) {
      setState({
        ...state,
        initialized: true,
      });

      window.__DCE[WT.calendar].init = false;
    }
  }, [calendar.model.slug]);

  useEffect(() => {
    if (hasSingleEvent) {
      fetchEvent(singleEventSlug, query.get("_s"));
    } else if (hasValidProps) {
      if (!skipInitialFetch) {
        if (hasInfiniteScroll) {
          onLoadMore(1);
        } else {
          _fetchEvents();
        }
      }
    }

    return () => {
      resetEventsCollection();
    };
  }, []);

  useEffect(() => {
    if (!hasSingleEvent && requestedWithSingleEvent) {
      setRequestedWithSingleEvent(false);
    }
  }, [hasSingleEvent]);

  useEffect(() => {
    setState({
      ...state,
      selectedDate: getSelectedDate(eventsNumberOption, eventsNumberCount),
    });
  }, [eventsNumberOption, eventsNumberCount]);

  useDidMountEffect(() => {
    if (calendar.model.template) {
      if (!hasSingleEvent) {
        if (hasInfiniteScroll) {
          onLoadMore(1, false);
        } else {
          resetEventsCollection();
          _fetchEvents();
        }
      }
    }
  }, [
    state.selectedDate.getUnixTime(),
    state.page,
    state.selectedFilters,
    calendar.model.show_private_events,
    calendar.model.show_events_with_images,
    calendar.model.show_private_events,
    calendar.model.filter_settings,
    calendar.model.has_featured_events,
    calendar.model.has_group_events_included,
    eventsNumberCount,
    hasInfiniteScroll,
    searchKeyword,
    requestedWithSingleEvent,
  ]);

  useEffect(() => {
    // console.log(">>>>>");

    if (requestedWithSingleEvent && events.fetchSuccess) {
      // console.log("setting selected date");
      setState({
        ...state,
        selectedDate: new Date(events.collection[0].start_time * 1000),
      });
    }
  }, [requestedWithSingleEvent, events.fetchSuccess]);

  // console.log("selected Date", state.selectedDate.format("YYYY-MM-DD"));

  /**
   *
   */
  const _fetchEvents = async () => {
    if (events.fetch || hasSingleEvent) {
      return false;
    }

    const fetchParams = getFetchEventsParams(
      filtersToQueryParam(state.selectedFilters),
      state.selectedDate,
      eventsNumberOption,
      eventsNumberCount,
      calendar.model.template,
      state.page
    );

    if (fetchParams) {
      return await fetchEventsWithFeaturedEvents(
        calendarUUID,
        fetchParams.startDate,
        fetchParams.endDate,
        fetchParams.limit,
        fetchParams.page,
        fetchParams.filters
      );
    }
  };

  const fetchEventsWithFeaturedEvents = async (
    calendarUUID,
    from,
    to,
    limit,
    page,
    filters
  ) => {
    if (Boolean(calendar.model?.widget_settings?.has_feature_highlights)) {
      const featuredParams = {};
      if (!embedded) {
        featuredParams.internal = 1;
      }

      fetchFeaturedEvents(
        calendarUUID,
        from,
        null,
        calendar.model?.widget_settings?.featured_events_limit || 12,
        1,
        filters,
        false,
        featuredParams
      );
    }

    const params = {
      include_featured: Boolean(calendar.model.has_featured_events),
    };

    if (searchKeyword.length) {
      params.keyword = searchKeyword;
    }

    if (!embedded) {
      params.internal = 1;
    }
    fetchEvents(calendarUUID, from, to, limit, page, filters, false, params);
  };

  /**
   *
   * @type {(function(*): Promise<void>)|*}
   */
  const fetchEventsByQuery = useCallback(async (query) => {
    searchEvents(calendarUUID, query);
  }, []);

  /**
   *
   * @type {(function(*): Promise<void>)|*}
   */
  const fetchEventsByKeyword = useCallback(
    async (keyword) => {
      setSearchKeyword(keyword);
    },
    [searchKeyword]
  );

  /**
   *
   * @param number month either 1 or -1 to change the month
   */
  const onSelectedDateChange = useCallback(
    (direction) => {
      if (hasInfiniteScroll) {
        resetEventsCollection();
      }

      let updatedDate =
        direction > 0
          ? state.selectedDate.addMonths(direction)
          : state.selectedDate.subtractMonths(direction * -1);

      // if it allows to show only upcoming events then don't allow to select past dates
      if (eventsNumberOption === "upcoming") {
        if (updatedDate < new Date()) {
          updatedDate = new Date();
        }
      }

      setState({
        ...state,
        selectedDate: getStartOfMonth(updatedDate),
      });
    },
    [hasInfiniteScroll, state]
  );

  /**
   *
   * @param date
   */
  const onSelectedWeekChange = (date) => {
    setState({
      ...state,
      selectedDate: date,
    });
    resetEventsCollection();
  };

  /**
   *
   * @param direction
   */
  const onUpcomingNavigationChange = useCallback(
    (direction) => {
      if (direction == 1) {
        const maxPages = Math.ceil(events.totalRows / eventsNumberCount);

        if (events.page >= maxPages) {
          return false;
        }
      }

      let page = state.page + direction;

      setState({
        ...state,
        page: page <= 1 ? 1 : page,
      });
    },
    [events.totalRows, events.page, eventsNumberCount, state]
  );

  /**
   *
   * @param location
   */
  const onChangeFilter = (filter) => {
    const selectedFilters = { ...state.selectedFilters };
    selectedFilters[filter.kind] = filter;

    setState({
      ...state,
      selectedFilters,
    });
  };

  /**
   *
   * @param page
   * @param preserveExistingEvents
   */
  const onLoadMore = (page, preserveExistingEvents = true) => {
    const showUpcomingOnlyNavigation = eventsNumberOption === "upcoming";

    const endMonth =
      showMonthSelector && isCTAllowed(calendar.model.template, [CT.eventList])
        ? getEndOfMonth(new Date(state.selectedDate))
        : null;

    const startDay = showUpcomingOnlyNavigation
      ? state.selectedDate < new Date()
        ? new Date()
        : new Date(state.selectedDate)
      : new Date(state.selectedDate);

    const filters = filtersToQueryParam(state.selectedFilters);

    const params = {
      include_featured: Boolean(calendar.model.has_featured_events),
    };

    if (searchKeyword.length) {
      params.keyword = searchKeyword;
    }

    if (!embedded) {
      params.internal = 1;
    }

    fetchEvents(
      calendarUUID,
      startDay,
      endMonth,
      10,
      page,
      filters,
      preserveExistingEvents,
      params
    );
  };

  /**
   * @param _page comes from the infinite scroller but it will not be accurate
   * if e.g. filters has changed. Such scenario will fetch 1st page from the backend but
   * on scroll the infinite scroller would return next number it had remembered rather than 2.
   * bottom line- do not rely on _page.
   */
  const onLoadMoreInfiniteScroller = (_page) => {
    if (events.collection.length >= events.totalRows) {
      return false;
    }

    const page = events.page + 1;

    onLoadMore(page);
  };

  const calendarType = calendar.model.template; /*singleEventSlug
    ? CT.eventList
    : ;*/

  const _onLoadMore =
    calendarType === CT.eventList
      ? singleEventSlug
        ? null
        : onLoadMoreInfiniteScroller
      : onLoadMoreInfiniteScroller;

  const _onSelectedDateChange =
    calendarType === CT.weekly ? onSelectedWeekChange : onSelectedDateChange;

  const CalendarComponent = CalendarComponentsMapping[calendarType];

  const featuredHighlights =
    Boolean(calendar.model.has_featured_events) &&
    Boolean(calendar.model.widget_settings.has_feature_highlights) &&
    featuredEvents.collection.length ? (
      <FeaturedHighlights
        key={calendar.model.widget_settings.featured_template || CT.bubble}
        embedded={embedded}
        calendar={calendar}
        events={featuredEvents}
        initialized={state.initialized}
        selectedFilters={state.selectedFilters}
        selectedDate={state.selectedDate}
        onChangeFilter={onChangeFilter}
        previewType={app.previewType}
        selectedEvent={singleEventSlug}
        eventsLimit={featuredHighlightsEventsLimit}
      />
    ) : null;

  const calendarComponent = (
    <React.Fragment>
      <BrandingHeader calendar={calendar} embedded={embedded} />
      {/*<BackToCalendar
        embedded={embedded}
        eventSlug={singleEventSlug}
      />*/}
      {featuredHighlights}
      <CalendarComponent
        embedded={embedded}
        calendar={calendar}
        events={events}
        initialized={state.initialized}
        searchEvents={fetchEventsByQuery}
        fetchEvents={_fetchEvents}
        selectedFilters={state.selectedFilters}
        selectedDate={state.selectedDate}
        onChangeFilter={onChangeFilter}
        previewType={app.previewType}
        onUpcomingNavigationChange={onUpcomingNavigationChange}
        selectedEvent={selectedEvent}
        onLoadMore={_onLoadMore}
        onSelectedDateChange={_onSelectedDateChange}
        onKeywordSearch={fetchEventsByKeyword}
        singleEventSlug={singleEventSlug}
      />
      <SelectedEventModal selectedEvent={selectedEvent} embedded={embedded} />
    </React.Fragment>
  );

  const content =
    app.previewType === PT.mobile ? (
      <div className={"mobile-preview--smartphone"}>{calendarComponent}</div>
    ) : (
      calendarComponent
    );

  const eventsSchema = events.collection.length ? (
    <EventSchema events={events.collection} />
  ) : null;

  return (
    <WidgetSettingsContext.Provider value={calendarSettings}>
      <CalendarLinkingContext.Provider value={calendarLinking}>
        {eventsSchema}
        <div
          className={classnames(
            widgetId,
            WT.calendar,
            `dce-${WT.calendar}`,
            `dce-${calendarType}`,
            {
              "mobile-preview": app.previewType === PT.mobile,
              /* "single-event": hasSingleEvent,*/
            }
          )}
          lang={i18n.language}
          role="region"
          aria-label={calendarPublicName(calendar.model)}
        >
          {content}
        </div>
      </CalendarLinkingContext.Provider>
    </WidgetSettingsContext.Provider>
  );
};

export default Calendar;
